import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { motion } from "framer-motion";
import { KeyRound, Loader2, Mail, ArrowRight, CheckCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const ResetPassword = () => {
  const [mode, setMode] = useState<"request" | "sent" | "reset">("request");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    // Check if user arrived via recovery link
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    if (hashParams.get("type") === "recovery") {
      setMode("reset");
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        setMode("reset");
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setLoading(false);

    if (error) {
      toast({ title: "خطأ", description: "تعذر إرسال رابط إعادة التعيين، حاول لاحقاً", variant: "destructive" });
    } else {
      setMode("sent");
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (/[^a-zA-Z\d!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(password)) {
      toast({ title: "كلمة المرور غير صالحة", description: "يجب أن تكون بالإنجليزية فقط", variant: "destructive" });
      return;
    }
    const passwordRegex = /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[^a-zA-Z\d]).{7,}$/;
    if (!passwordRegex.test(password)) {
      toast({ title: "كلمة المرور ضعيفة", description: "يجب أن تحتوي على 7 خانات على الأقل من أحرف وأرقام ورموز", variant: "destructive" });
      return;
    }
    if (password !== confirmPassword) {
      toast({ title: "غير متطابقة", description: "تأكد من تطابق كلمتي المرور", variant: "destructive" });
      return;
    }

    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password });
    setLoading(false);

    if (error) {
      toast({ title: "خطأ", description: "تعذر تحديث كلمة المرور، حاول مرة أخرى", variant: "destructive" });
    } else {
      toast({ title: "تم بنجاح!", description: "تم تغيير كلمة المرور" });
      navigate("/tree");
    }
  };

  if (mode === "sent") {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="w-full max-w-md">
          <Card className="glass-card border-primary/10 rounded-2xl modern-shadow">
            <CardHeader className="text-center pb-2">
              <div className="mx-auto mb-4 p-4 rounded-2xl bg-primary/10 w-fit">
                <Mail className="h-8 w-8 text-primary" />
              </div>
              <CardTitle className="font-amiri text-2xl gold-text">تحقق من بريدك الإلكتروني</CardTitle>
              <p className="text-muted-foreground mt-3 text-sm leading-relaxed">
                تم إرسال رابط إعادة تعيين كلمة المرور إلى
                <br />
                <span className="text-primary font-medium" dir="ltr">{email}</span>
                <br /><br />
                افتح بريدك الإلكتروني واضغط على الرابط لتعيين كلمة مرور جديدة.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-xs text-muted-foreground text-center">
                لم يصلك البريد؟ تحقق من مجلد الرسائل غير المرغوب فيها (Spam)
              </p>
              <Button variant="outline" className="w-full rounded-xl" onClick={() => navigate("/login")}>
                <ArrowRight className="h-4 w-4 ml-2" />
                العودة لتسجيل الدخول
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  if (mode === "reset") {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="w-full max-w-md">
          <Card className="glass-card border-primary/10 rounded-2xl modern-shadow">
            <CardHeader className="text-center pb-2">
              <div className="mx-auto mb-4 p-4 rounded-2xl bg-primary/10 w-fit">
                <CheckCircle className="h-8 w-8 text-primary" />
              </div>
              <CardTitle className="font-amiri text-3xl gold-text">كلمة مرور جديدة</CardTitle>
              <p className="text-muted-foreground mt-2 text-sm">أدخل كلمة المرور الجديدة لحسابك</p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleResetPassword} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="newPassword">كلمة المرور الجديدة</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="bg-secondary/50 border-primary/10 rounded-xl"
                    dir="ltr"
                    required
                    minLength={7}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmNewPassword">تأكيد كلمة المرور</Label>
                  <Input
                    id="confirmNewPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    className="bg-secondary/50 border-primary/10 rounded-xl"
                    dir="ltr"
                    required
                    minLength={7}
                  />
                  {confirmPassword && password !== confirmPassword && (
                    <p className="text-xs text-destructive">كلمتا المرور غير متطابقتين</p>
                  )}
                </div>
                <Button type="submit" className="w-full gold-gradient text-primary-foreground rounded-xl" disabled={loading}>
                  {loading ? <Loader2 className="h-4 w-4 animate-spin ml-2" /> : null}
                  تعيين كلمة المرور
                </Button>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="w-full max-w-md">
        <Card className="glass-card border-primary/10 rounded-2xl modern-shadow">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto mb-4 p-4 rounded-2xl bg-primary/10 w-fit">
              <KeyRound className="h-8 w-8 text-primary" />
            </div>
            <CardTitle className="font-amiri text-3xl gold-text">نسيت كلمة المرور</CardTitle>
            <p className="text-muted-foreground mt-2 text-sm">أدخل بريدك الإلكتروني وسنرسل لك رابط إعادة التعيين</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleRequestReset} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="resetEmail">البريد الإلكتروني</Label>
                <Input
                  id="resetEmail"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="example@email.com"
                  className="bg-secondary/50 border-primary/10 rounded-xl"
                  dir="ltr"
                  required
                />
              </div>
              <Button type="submit" className="w-full gold-gradient text-primary-foreground rounded-xl" disabled={loading}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin ml-2" /> : null}
                إرسال رابط إعادة التعيين
              </Button>
            </form>
            <div className="mt-4 text-center">
              <button type="button" className="text-sm text-primary hover:underline" onClick={() => navigate("/login")}>
                العودة لتسجيل الدخول
              </button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default ResetPassword;
